/**
 * Analyzer: Day News & Economic Event Impact
 *
 * Reads the day's news events (journalEntry.newsEvents) and today's manual
 * eventTags, then cross-references historical days that shared the same
 * event tags. Surfaces:
 *   - "Similar event-day edge" when today's P&L beats the average of those days
 *   - "Similar event-day drag" when today underperforms
 *   - "Trades near event" callout when trades were taken within 30min of
 *     any event, noting their aggregate outcome
 *
 * Does NOT fire when there's no event history — silently skips.
 */

import { CATEGORY, SEVERITY, makeObservation } from '../../types';
import { parseHM } from '../../utils';
import { NEWS_PROXIMITY_MIN } from '../../dayUtils';

export const key = 'dayNewsEventImpact';
export const tier = 'free';

/**
 * For each trade in `day.trades`, find news events whose time is within
 * NEWS_PROXIMITY_MIN of the trade's entry time.
 */
const tradesNearEvents = (day) => {
  const events = Array.isArray(day.journalEntry?.newsEvents) ? day.journalEntry.newsEvents : [];
  if (events.length === 0) return [];

  const rows = [];
  for (const trade of day.trades) {
    const entryMins = parseHM(trade.entryTime);
    if (entryMins === null) continue;
    for (const ev of events) {
      const evMins = parseHM(ev.time);
      if (evMins === null) continue;
      if (Math.abs(entryMins - evMins) <= NEWS_PROXIMITY_MIN) {
        rows.push({ trade, event: ev, deltaMin: entryMins - evMins });
      }
    }
  }
  return rows;
};

export const analyze = (ctx) => {
  const { day, allJournalEntries = {}, allTrades = [], getTradeNetPnL, getAccountById } = ctx;
  if (!day || !day.core) return null;
  // Live-trade gate: skip when no trades have closed today. v7.5.14 contract
  // extended to day-scope; see dayUtils.js for the closed-only core stats.
  if (day.core.closedTotal === 0) return null;

  const out = [];

  // ── 1. Trades taken near events ─────────────────────────────────────────
  const near = tradesNearEvents(day);
  if (near.length > 0) {
    const pnlNear = near.reduce((s, r) => s + (r.trade.netPnl || 0), 0);
    const winsNear = near.filter(r => (r.trade.netPnl || 0) > 0).length;
    const tradeIds = new Set(near.map(r => r.trade.id));
    const eventNames = [...new Set(near.map(r => r.event.name))];
    const topEvent = eventNames[0];
    const eventLabel = eventNames.length > 1 ? `${topEvent} +${eventNames.length - 1} more` : topEvent;
    const winRateNear = tradeIds.size > 0 ? (winsNear / tradeIds.size * 100).toFixed(0) : '0';

    const severity = pnlNear < 0 ? SEVERITY.WARNING : SEVERITY.INFO;

    out.push(makeObservation({
      id: `${key}:${day.date}:near`,
      analyzer: key,
      scope: 'day',
      scopeId: day.date,
      severity,
      category: CATEGORY.EXECUTION,
      title: `${tradeIds.size} trade${tradeIds.size > 1 ? 's' : ''} taken near ${eventLabel}`,
      detail: `Within ${NEWS_PROXIMITY_MIN}min of release: ${pnlNear >= 0 ? '+' : '−'}$${Math.abs(pnlNear).toFixed(0)} @ ${winRateNear}% WR.`,
      action: pnlNear < 0
        ? 'Event-chop bleed adds up. Consider a 5-min no-fly zone around high-impact releases.'
        : 'Trading into the print worked today — log the read (direction, strength, reversal tell) while it\'s fresh.',
      evidence: { near, pnlNear, tradeCount: tradeIds.size, events: eventNames },
      metric: { label: 'Near-Event P&L', value: `${pnlNear >= 0 ? '+' : '−'}$${Math.abs(pnlNear).toFixed(0)}`, unit: '' },
      confidence: 0.8,
      priority: 75
    }));
  }

  // ── 2. Similar event-day performance ────────────────────────────────────
  const todayTags = Array.isArray(day.journalEntry?.eventTags) ? day.journalEntry.eventTags : [];
  if (todayTags.length > 0 && Object.keys(allJournalEntries).length > 1 && Array.isArray(allTrades)) {
    // Find other days that shared any of today's event tags
    const similarDates = Object.entries(allJournalEntries)
      .filter(([d, entry]) =>
        d !== day.date &&
        Array.isArray(entry?.eventTags) &&
        entry.eventTags.some(tag => todayTags.includes(tag)))
      .map(([d]) => d);

    if (similarDates.length >= 2) {
      // Historic P&L on those days — aggregated PER-DAY (not per-trade) so
      // the average isn't pulled around by trade volume. The previous
      // implementation divided total-trade-P&L by `similarDates.length`,
      // which silently treated tag-only days (event chip set but zero trades
      // taken) as $0 entries in the denominator, dragging the average toward
      // zero. Now we only count days that actually have trades, and we expose
      // best/worst day for richer evidence.
      const netFor = (t) => {
        const acc = getAccountById ? getAccountById(t.accountId) : null;
        const r = getTradeNetPnL ? getTradeNetPnL(t, acc) : { netPnl: parseFloat(t.pnl) || 0 };
        return isFinite(r?.netPnl) ? r.netPnl : 0;
      };
      const dateSet = new Set(similarDates);
      const byDate = {};
      for (const t of allTrades) {
        if (!dateSet.has(t.date)) continue;
        if (!byDate[t.date]) byDate[t.date] = { pnl: 0, trades: 0 };
        byDate[t.date].pnl += netFor(t);
        byDate[t.date].trades += 1;
      }
      const dayPnls = Object.values(byDate).map(d => d.pnl);
      const tradedDayCount = dayPnls.length;

      if (tradedDayCount >= 2) {
        const avgHistoric = dayPnls.reduce((s, p) => s + p, 0) / tradedDayCount;
        const negDays = dayPnls.filter(p => p < 0).length;
        const bestDay = dayPnls.reduce((b, p) => p > b ? p : b, dayPnls[0]);
        const worstDay = dayPnls.reduce((w, p) => p < w ? p : w, dayPnls[0]);
        const diff = day.core.totalPnL - avgHistoric;
        const sharedTag = todayTags.find(tag =>
          similarDates.some(d => allJournalEntries[d]?.eventTags?.includes(tag))
        ) || todayTags[0];

        if (Math.abs(diff) >= 50) {
          const beat = diff > 0;
          out.push(makeObservation({
            id: `${key}:${day.date}:similar:${sharedTag}`,
            analyzer: key,
            scope: 'day',
            scopeId: day.date,
            severity: beat ? SEVERITY.SUCCESS : SEVERITY.WARNING,
            category: CATEGORY.VARIANCE,
            title: beat
              ? `Beat your typical "${sharedTag}" day`
              : `Underperformed your typical "${sharedTag}" day`,
            detail: `Across ${tradedDayCount} prior "${sharedTag}" days you averaged ${avgHistoric >= 0 ? '+' : '−'}$${Math.abs(avgHistoric).toFixed(0)} (${negDays}/${tradedDayCount} red). Today: ${day.core.totalPnL >= 0 ? '+' : '−'}$${Math.abs(day.core.totalPnL).toFixed(0)} (${diff >= 0 ? '+' : '−'}$${Math.abs(diff).toFixed(0)} vs avg).`,
            action: beat
              ? `You just outperformed your "${sharedTag}" baseline. Note the read so this stops being variance.`
              : 'Pull up a prior "' + sharedTag + '" day that went well — what did you do differently?',
            evidence: {
              sharedTag, similarDates, tradedDayCount, avgHistoric, bestDay, worstDay, negDays,
              todayPnL: day.core.totalPnL, diff
            },
            metric: { label: 'vs Event Avg', value: `${diff >= 0 ? '+' : '−'}$${Math.abs(diff).toFixed(0)}`, unit: '' },
            confidence: Math.min(0.9, 0.5 + tradedDayCount * 0.05),
            priority: beat ? 55 : 70
          }));
        }
      }
    }
  }

  return out.length > 0 ? out : null;
};
